import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccountCircleRounded,
  AdminPanelSettingsRounded,
  EmailRounded,
  KeyRounded,
  LockResetRounded,
  LockRounded,
  SecurityRounded,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_SECONDARY,
  COLOR_VERY_LIGHT_GRAY,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { HeaderView } from "../../component/HeaderView";
import { CustomTextView } from "../../component/CustomTextView";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextField } from "../../component/CustomTextField";
import { CustomButtonView } from "../../component/CustomButtonView";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import {
  ADMIN_PANEL_EMAIL,
  MAIN_FONT_FAMILY,
  STORAGE_KEYS,
} from "../../../data/constant/Constants";
import { useSnackbar } from "notistack";
import { Validation } from "../../../domain/validation/Validation";

export function ProfileView() {
  const { enqueueSnackbar } = useSnackbar();

  const storedEmail =
    SharedPreferences.getString(STORAGE_KEYS.USER_EMAIL) || ADMIN_PANEL_EMAIL;
  const adminName = storedEmail.split("@")[0] || "Administrator";
  const formattedName =
    adminName.charAt(0).toUpperCase() + adminName.slice(1);

  // Change Password Form State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePasswordInputs = () => {
    const errs: { [key: string]: string } = {};
    if (!currentPassword) {
      errs.currentPassword = "Current password is required";
    }
    if (!newPassword) {
      errs.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      errs.newPassword = "Password must be at least 8 characters";
    }
    if (!confirmPassword) {
      errs.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }
    return errs;
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validatePasswordInputs();
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setIsSubmitting(true);
      // Change password UI action - ready for API integration
      setTimeout(() => {
        setIsSubmitting(false);
        enqueueSnackbar(
          "Password change request simulated. Ready for backend API integration.",
          { variant: "success" }
        );
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setErrors({});
      }, 600);
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <HeaderView
          title={"My Profile"}
          layout={<Box />}
          hasDivider={true}
        />

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
          <Grid container spacing={3}>
            {/* Profile Overview Card */}
            <Grid
              size={{
                xs: 12,
                md: 5,
                lg: 4,
              }}
            >
              <CustomCardView padding={3} borderRadius={6} background={COLOR_WHITE}>
                <Stack spacing={3} alignItems="center" textAlign="center">
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      backgroundColor: COLOR_MAIN,
                      color: COLOR_WHITE,
                      fontSize: 38,
                      fontWeight: "bold",
                      boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                    }}
                  >
                    {formattedName.charAt(0)}
                  </Avatar>

                  <Stack spacing={0.5} alignItems="center">
                    <CustomTextView
                      text={formattedName}
                      variant="h6"
                      fontWeight="bold"
                      textColor={COLOR_BLACK}
                    />
                    <CustomTextView
                      text={storedEmail}
                      variant="body2"
                      textColor={COLOR_LIGHT_GRAY}
                    />
                    <Box pt={1}>
                      <Chip
                        icon={<AdminPanelSettingsRounded sx={{ fontSize: 16 }} />}
                        label="System Admin"
                        size="small"
                        sx={{
                          backgroundColor: COLOR_VERY_LIGHT_GRAY,
                          color: COLOR_MAIN,
                          fontWeight: "bold",
                          fontFamily: MAIN_FONT_FAMILY,
                        }}
                      />
                    </Box>
                  </Stack>

                  <Divider sx={{ width: "100%", borderColor: COLOR_VERY_LIGHT_GRAY }} />

                  <Stack spacing={2} width="100%" textAlign="left">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <EmailRounded sx={{ color: COLOR_MAIN, fontSize: 20 }} />
                      <Stack>
                        <CustomTextView
                          text="Email"
                          variant="caption"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                        <CustomTextView
                          text={storedEmail}
                          variant="body2"
                          fontWeight="600"
                          textColor={COLOR_BLACK}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <SecurityRounded sx={{ color: COLOR_MAIN, fontSize: 20 }} />
                      <Stack>
                        <CustomTextView
                          text="Role"
                          variant="caption"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                        <CustomTextView
                          text="Administrator (Role.ADMIN)"
                          variant="body2"
                          fontWeight="600"
                          textColor={COLOR_BLACK}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AccountCircleRounded sx={{ color: COLOR_MAIN, fontSize: 20 }} />
                      <Stack>
                        <CustomTextView
                          text="Access Level"
                          variant="caption"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                        <CustomTextView
                          text="Full Access (All Modules)"
                          variant="body2"
                          fontWeight="600"
                          textColor={COLOR_BLACK}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </CustomCardView>
            </Grid>

            {/* Change Password & Security Settings */}
            <Grid
              size={{
                xs: 12,
                md: 7,
                lg: 8,
              }}
            >
              <CustomCardView padding={3.5} borderRadius={6} background={COLOR_WHITE}>
                <Stack spacing={3}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <LockResetRounded sx={{ color: COLOR_MAIN, fontSize: 28 }} />
                    <Stack>
                      <CustomTextView
                        text="Change Password"
                        variant="h6"
                        fontWeight="bold"
                        textColor={COLOR_BLACK}
                      />
                      <CustomTextView
                        text="Update your admin account password to keep your portal secure"
                        variant="body2"
                        textColor={COLOR_LIGHT_GRAY}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ borderColor: COLOR_VERY_LIGHT_GRAY }} />

                  <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
                    <Stack spacing={2.5}>
                      <Stack spacing={0.5}>
                        <CustomTextView
                          text="Current Password"
                          variant="body2"
                          fontWeight="bold"
                          textColor={COLOR_BLACK}
                        />
                        <CustomTextField
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="Enter current password"
                          value={currentPassword}
                          onTextChange={setCurrentPassword}
                          error={!!errors.currentPassword}
                          helperText={errors.currentPassword}
                          startIcon={<LockRounded sx={{ color: COLOR_LIGHT_GRAY }} />}
                          endIcon={
                            <IconButton
                              size="small"
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              edge="end"
                            >
                              {showCurrentPassword ? (
                                <VisibilityOff sx={{ fontSize: 18 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          }
                          borderColor={COLOR_LIGHT_GRAY}
                          backgroundColor={COLOR_WHITE}
                        />
                      </Stack>

                      <Stack spacing={0.5}>
                        <CustomTextView
                          text="New Password"
                          variant="body2"
                          fontWeight="bold"
                          textColor={COLOR_BLACK}
                        />
                        <CustomTextField
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Enter new password (min. 8 characters)"
                          value={newPassword}
                          onTextChange={setNewPassword}
                          error={!!errors.newPassword}
                          helperText={errors.newPassword}
                          startIcon={<KeyRounded sx={{ color: COLOR_LIGHT_GRAY }} />}
                          endIcon={
                            <IconButton
                              size="small"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityOff sx={{ fontSize: 18 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          }
                          borderColor={COLOR_LIGHT_GRAY}
                          backgroundColor={COLOR_WHITE}
                        />
                      </Stack>

                      <Stack spacing={0.5}>
                        <CustomTextView
                          text="Confirm New Password"
                          variant="body2"
                          fontWeight="bold"
                          textColor={COLOR_BLACK}
                        />
                        <CustomTextField
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Re-enter new password"
                          value={confirmPassword}
                          onTextChange={setConfirmPassword}
                          error={!!errors.confirmPassword}
                          helperText={errors.confirmPassword}
                          startIcon={<KeyRounded sx={{ color: COLOR_LIGHT_GRAY }} />}
                          endIcon={
                            <IconButton
                              size="small"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff sx={{ fontSize: 18 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          }
                          borderColor={COLOR_LIGHT_GRAY}
                          backgroundColor={COLOR_WHITE}
                        />
                      </Stack>

                      <Box pt={1}>
                        <CustomButtonView
                          text="Update Password"
                          type="submit"
                          loading={isSubmitting}
                          fontSize="body1"
                          props={{
                            py: 1.2,
                            px: 4,
                            borderRadius: 3,
                            maxWidth: 220,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CustomCardView>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
}
